import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../image/logo.png';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(newLang);
  };

  const navLinks = [
    { id: 'home', labelKey: 'nav_home' },
    { id: 'products', labelKey: 'nav_products' },
    { id: 'design', labelKey: 'nav_design' },
    { id: 'partners', labelKey: 'nav_partners' },
    { id: 'contact', labelKey: 'nav_contact' },
  ];

  const isScrolledOrOpen = scrolled || menuOpen;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolledOrOpen ? 'bg-[#faf8f5] shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <img
                  src = {logo}
                  alt="Logo"
                  className="h-10 w-auto"
                />
              </div>
              <div className="flex flex-col justify-center">
                <span
                  className={`text-[15px] font-semibold tracking-[0.18em] uppercase transition-colors duration-300 ${isScrolledOrOpen ? 'text-[#2a1a0e]' : 'text-white'}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.22em' }}
                >
                  Cyan Charm
                </span>
                <div className="flex items-center gap-1.5 mt-[2px]">
                  <div className={`h-px flex-1 transition-colors duration-300 ${isScrolledOrOpen ? 'bg-[#c9a96e]' : 'bg-[#f0d9b5]/70'}`}></div>
                  <span
                    className={`text-[9px] tracking-[0.35em] transition-colors duration-300 ${isScrolledOrOpen ? 'text-[#b8864e]' : 'text-[#f0d9b5]'}`}
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    青韵千艺
                  </span>
                  <div className={`h-px flex-1 transition-colors duration-300 ${isScrolledOrOpen ? 'bg-[#c9a96e]' : 'bg-[#f0d9b5]/70'}`}></div>
                </div>
              </div>
            </div>
          </div>

          {/* 桌面端导航 */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-[#3a2a1a] hover:text-[#b8864e]' : 'text-white hover:text-[#f0d9b5]'
                }`}
              >
                {t(link.labelKey)}
              </button>
            ))}

            {/* 语言切换按钮 */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1.5 text-sm font-medium border rounded-full px-3 py-1 transition-all whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'border-[#c9a96e] text-[#3a2a1a] hover:border-[#b8864e] hover:text-[#b8864e]'
                  : 'border-white/60 text-white hover:border-white hover:bg-white/10'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-translate-2 text-sm"></i>
              </div>
              <span>{i18n.language === 'zh' ? 'EN' : '中文'}</span>
            </button>
          </div>

          {/* 手机端右侧：语言切换 + 汉堡 */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 text-xs font-medium border rounded-full px-2.5 py-1 transition-all whitespace-nowrap cursor-pointer ${
                isScrolledOrOpen
                  ? 'border-[#c9a96e] text-[#3a2a1a]'
                  : 'border-white/60 text-white'
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-translate-2 text-xs"></i>
              </div>
              <span>{i18n.language === 'zh' ? 'EN' : '中文'}</span>
            </button>
            <button
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="菜单"
            >
              <i className={`text-2xl ${isScrolledOrOpen ? 'text-[#3a2a1a]' : 'text-white'} ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* 手机端下拉菜单 */}
        {menuOpen && (
          <div className="md:hidden pt-4 pb-2 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-left text-sm font-medium text-[#3a2a1a] hover:text-[#b8864e] transition-colors py-3 px-2 border-b border-[#e8ddd0] whitespace-nowrap cursor-pointer"
              >
                {t(link.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
